import { get, put, post } from "@/services/api.services";
import { EntityLookupResult } from "@/services/commonModels";

export type BenchlingFermenter = {
    id: string;
    name: string;
    fermenter: string;
    fermenter_code: string;
    vendor: string | null;
};

export type BenchlingProtocol = {
    protocol_id: string;
    registry_id: string;
    name: string;
    template_id: string | null;
    plant: string | null;
    insect: string | null;
    bacteria: string | null;
    fungus: string | null;
    delivery: string | null;
    concentrations: unknown;
    concentration_units: string | null;
    filterbatchlist: number | null;
    uploadfile: number | null;
    container_type: string | null;
    project_id: string | null;
    plot_randomize: boolean;
    rows: number | null;
    columns: number | null;
    functional_team: string | null;
    protocol_type: string | null;
    display_plate: string | null;
    sample_descriptor_list: string[] | null;
    material_type_list: string[] | null;
    url$: string | null;
    alternate_container_type: string | null;
    wells_per_rep: string | null;
    row_or_column_major: string | null;
    pad_plate: string | null;
    study_design: string | null;
    sample_workflow: string | null;
    control_array: string | null;
    set_conditions_on_containers: boolean;
};

export type BenchlingProjectFolderOption = {
    folderId: string;
    projectId: string;
    path: string;
};

type EntitySchemaDataRecord = {
    schema_id: string;
    schema_name: string;
};

export const fetchAllBenchlingProjects = () => {
    return get("/benchling/projects");
};

export async function fetchContainerTypes() {
    return get("/container/types");
}

export async function fetchDropdownValues(dropdownName: string) {
    return get(`/benchling/dropdowns/${encodeURIComponent(dropdownName)}`);
}

export async function fetchFermenters(): Promise<BenchlingFermenter[]> {
    return get(`/benchling/registries/fermenters`);
}

export async function fetchFermenterRunProtocols(): Promise<BenchlingProtocol[]> {
    return get("/benchling/registries/protocols?protocolType=Fermenter%20Run");
}

export async function fetchIncubators() {
    return get("/experiment/fetch/incubators");
}

export async function fetchProtocols(): Promise<BenchlingProtocol[]> {
    return get("/benchling/registries/protocols");
}

export async function fetchBatchesByCodes(batchNames: string[]): Promise<EntityLookupResult> {
    return post("/benchling/batches/lookupBatches", { batchNames });
}

export const benchlingAPI = {
    fetchProjectFolderOptions(): Promise<BenchlingProjectFolderOption[]> {
        return get(`/benchling/projectFolderOptions`);
    },
    fetchExperimentMetadataFieldsForSchemaId(schemaId: string) {
        return get(`/benchling/schema_metadata_fields`, { params: { schemaId: schemaId } });
    },
    async fetchEntityDataSchemaData(entityId: string) {
        const data = (await get(
            `/benchling/get_entity_schema_data/${entityId}`
        )) as EntitySchemaDataRecord[][];

        // No clue why this is only picking indexed bits of the response.
        return {
            schemaType: data[1],
            schemaId: data[0][0].schema_id,
            schemaName: data[0][0].schema_name,
        };
    },
    fetchTableEntitiesForEntityTable(entityTable: string) {
        return get(`/benchling/table_entities/${entityTable}`);
    },
    fetchSchemaMetadataForSchemaIdAndEntityId(schemaId: string, entityId: string) {
        return get(`/benchling/schema_metadata`, {
            params: {
                schemaId,
                entityId,
            },
        });
    },
    /**
     * customEntity = {
     *    fields: {
     *        fieldName: {
     *            value: value,
     *        }
     *    },
     *    schemaId: this.schemaId
     * };
     */
    updateCustomEntity(entityId: string, customEntity: unknown) {
        return put(`/benchling/updateCustomEntity/${entityId}`, customEntity);
    },
    /**
     * customEntity = {
     *    fields: {
     *        fieldName: {
     *            value: value,
     *        }
     *    },
     *    schemaId: this.schemaId
     * };
     */
    updateEntry(entityId: string, customEntity: unknown) {
        return put(`/benchling/updateExperiment/${entityId}`, customEntity);
    },
};
